/**
 * SecurityPage.tsx — MER account security. Phase-2 P2-D4.
 *
 * ── Deliberately minimal ─────────────────────────────────────────────────────
 * The boundary banked at D-P2AA-5 puts ONE capability here: changing the
 * password. This is not an account-settings page and must not grow into one —
 * profile editing, account deletion, role management and onboarding belong to
 * P2-D13 and P2-D14, and pulling them here would silently widen P2-D4.
 *
 * ── Passwords live in component state and nowhere else ───────────────────────
 * Never in the URL, never in localStorage or sessionStorage, never logged. They
 * are cleared as soon as the change succeeds.
 *
 * ── The qualified success ────────────────────────────────────────────────────
 * `reauthenticationRequired` means the change COMMITTED but the session could
 * not be re-stamped. It is reported as a success that requires signing in
 * again, never as a failure — telling a user their password change failed when
 * it actually succeeded is how people get locked out of their own account.
 */
import React, { useState, useEffect, useRef, FormEvent } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth, ACCOUNT_DISABLE_CONFIRMATION } from '../../auth/AuthContext';

const REASON_MESSAGES: Record<string, string> = {
  CURRENT_PASSWORD_INVALID: 'That is not your current password.',
  NETWORK_ERROR:            'Network error — check your connection.',
};

/**
 * Phase-2 P2-D14 slice 3. Messages for the terminal action.
 *
 * Separate from REASON_MESSAGES above because one of these entries has to say
 * something REASON_MESSAGES must never say. Permanent disable is not
 * repeatable, so a transport failure after submission leaves the outcome
 * genuinely unknown — the commit may already have happened. The generic
 * "check your connection" would invite a retry, and a retry is the one thing
 * that must not happen here.
 *
 * Every reason code the server route can return is mapped, with ONE deliberate
 * exception. `p2D14SelfServiceLifecycleUi.test.js` (U18-U21) extracts the
 * route's refusal set from `authRoutes.js` and fails if a code is neither
 * mapped here nor in its documented exception set, so adding a server refusal
 * cannot silently degrade this surface to the fallback.
 *
 * ── The exception: PASSWORD_CREDENTIAL_NOT_CONFIGURED ───────────────────────
 * Deliberately absent, for two reasons that agree.
 *
 * It is unreachable from this UI. The server sets `canDisableAccount` false
 * for a passwordless account, so the control is never rendered to anyone who
 * could provoke that code; only a direct API call reaches it, and a direct
 * caller is not reading these strings.
 *
 * And the banked P2-D6 assertion `P2D6-D9ux-D10ux` forbids that constant
 * appearing anywhere in this file, because the Google-link ceremony must
 * answer a rejection generically rather than echo server reason codes. That
 * assertion scans the whole file, so mapping the code here — even as a lookup
 * key that is never displayed — would break it. The banked test is not edited
 * to accommodate this; §13 asks only that EXPLICITLY SUPPORTED codes be
 * mapped, and an unreachable one is not supported. It falls to the generic
 * message below, which is accurate: nothing changed.
 *
 * Nothing here mentions a transaction, an epoch or a session identifier.
 */
const DISABLE_REASON_MESSAGES: Record<string, string> = {
  CURRENT_PASSWORD_INVALID:          'That is not your current password.',
  VALIDATION_ERROR:                  'Enter your current password to continue.',
  CONFIRMATION_REQUIRED:             'Type the confirmation phrase exactly as shown.',
  ACCOUNT_INACTIVE:                  'This account is already inactive.',
  ALREADY_PERMANENTLY_DISABLED:      'This account has already been permanently disabled.',
  LAST_ADMIN_LOCKED:
    'You are the only active administrator. Another administrator must be active before you can disable this account.',
  NOT_AUTHENTICATED:                 'Your session has ended. Please sign in again.',
  RATE_LIMITED:                      'Too many attempts. Wait a few minutes and try again.',
  ACCOUNT_DISABLE_REFUSED:           'That request was refused. Nothing has changed.',
  // Deliberately claims NEITHER outcome. See above.
  NETWORK_ERROR:
    'The request could not be confirmed. Please sign in again or refresh your account state before trying another action.',
};

const LABEL: React.CSSProperties = {
  fontSize: 10, letterSpacing: 2, color: '#3A5070', display: 'block', marginBottom: 6,
};
const INPUT: React.CSSProperties = {
  width: '100%', padding: '10px 12px', fontSize: 13,
  background: '#0A1628', border: '1px solid #1A3050',
  borderRadius: 3, color: '#E8EDF5', fontFamily: 'inherit', outline: 'none',
};

export function SecurityPage() {
  const {
    changePassword, requestEmailVerification, user,
    startGoogleLink, finalizeGoogleLink, unlinkGoogle, refreshSession,
    disableAccount,
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // ── Phase-2 P2-D5: email verification status and request ──────────────────
  // Status only. It is recorded identity assurance (D-P2AC-1), so nothing on
  // this page may present it as a restriction, and an unverified account is
  // "not yet verified" — never blocked, invalid or untrusted.
  const [verifySent,    setVerifySent]    = useState(false);
  const [verifyError,   setVerifyError]   = useState<string | null>(null);
  const [verifyLoading, setVerifyLoading] = useState(false);

  async function handleVerify() {
    setVerifyError(null);
    setVerifyLoading(true);
    const result = await requestEmailVerification();
    setVerifyLoading(false);
    if (result.ok) { setVerifySent(true); return; }
    setVerifyError(
      result.reason === 'VERIFICATION_UNAVAILABLE'
        ? 'Email verification is not currently available.'
        : 'Could not send the verification email. Please try again.',
    );
  }

  // ── Phase-2 P2-D6: Google account linking ─────────────────────────────────
  // Two halves separated by a round trip through Google. This page runs the
  // start ceremony, then recognises the generic return marker and completes it.
  const [showLinkForm, setShowLinkForm] = useState(false);
  const [linkPassword, setLinkPassword] = useState('');
  const [linkBusy,     setLinkBusy]     = useState(false);
  const [linkError,    setLinkError]    = useState<string | null>(null);
  const [linkNotice,   setLinkNotice]   = useState<string | null>(null);

  async function handleStartLink(e: FormEvent) {
    e.preventDefault();
    setLinkError(null);
    setLinkBusy(true);
    const result = await startGoogleLink(linkPassword);
    // Cleared on BOTH paths, immediately. The value has done its only job.
    setLinkPassword('');
    if (result.ok && result.authorizationUrl) {
      window.location.assign(result.authorizationUrl);
      return;
    }
    setLinkBusy(false);
    // One message for every refusal. Which rule declined — wrong password,
    // ineligible credential, feature state — is audit-internal (D-P2AJ-24).
    setLinkError('Could not start Google linking. Please try again.');
  }

  // ── Phase-2 P2-D6 IMPL-4: unlink ──────────────────────────────────────────
  // Deliberately NOT presented as the mirror image of linking. Unlinking
  // revokes every other session, so the confirmation says so before the user
  // commits to it — a surprise sign-out on another device is the kind of thing
  // people report as a bug rather than recognise as the feature working.
  const [showUnlinkForm, setShowUnlinkForm] = useState(false);
  const [unlinkPassword, setUnlinkPassword] = useState('');
  const [unlinkBusy,     setUnlinkBusy]     = useState(false);
  const [unlinkError,    setUnlinkError]    = useState<string | null>(null);

  async function handleUnlink(e: FormEvent) {
    e.preventDefault();
    setUnlinkError(null);
    setUnlinkBusy(true);
    const result = await unlinkGoogle(unlinkPassword);
    setUnlinkPassword('');
    setUnlinkBusy(false);

    if (!result.ok) {
      setUnlinkError('Could not remove Google. Please try again.');
      return;
    }

    setShowUnlinkForm(false);
    if (result.reauthenticationRequired) {
      // The revocation IS durable. Reporting failure would invite the user to
      // repeat an operation that already succeeded.
      setLinkNotice('Google was removed, but you must sign in again.');
      setTimeout(() => navigate('/login', { replace: true }), 2500);
      return;
    }
    setLinkNotice(result.alreadyUnlinked ? 'Google is not linked.' : 'Google was removed.');
    await refreshSession();
  }

  // ── Phase-2 P2-D14 slice 3: permanently disable this account ──────────────
  //
  // A TWO-STEP CEREMONY, and the two steps are not decoration. The control in
  // the section only opens the form; the network call is reachable from the
  // submit handler alone, so no single click can end an account.
  //
  // `disableOnce` is claimed BEFORE the await rather than after. A state flag
  // set after an await has not landed while the request is in flight, which is
  // exactly how a double click produces two requests — the same ordering
  // mistake this page already had to fix once in the Google finalize effect.
  // For an action that cannot be repeated safely, a second request is not a
  // cosmetic problem.
  const [showDisableForm, setShowDisableForm] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');
  const [disablePhrase,   setDisablePhrase]   = useState('');
  const [disableBusy,     setDisableBusy]     = useState(false);
  const [disableError,    setDisableError]    = useState<string | null>(null);
  const disableOnce = useRef(false);

  /** Leave the ceremony, keeping no trace of the password. */
  function cancelDisable() {
    setShowDisableForm(false);
    setDisablePassword('');
    setDisablePhrase('');
    setDisableError(null);
  }

  async function handleDisableAccount(e: FormEvent) {
    e.preventDefault();
    if (disableBusy || disableOnce.current) return;
    disableOnce.current = true;
    setDisableBusy(true);
    setDisableError(null);

    const result = await disableAccount(disablePassword);

    // Cleared on BOTH paths, immediately. The password has done its only job.
    setDisablePassword('');

    if (result.ok) {
      // The account is terminal. There is no session to keep, nothing to
      // refresh and nothing to undo — AuthContext has already cleared the
      // client model, so the only correct move is to leave. No /api/me
      // re-read: it would be asking about an account that can no longer
      // answer.
      navigate('/login', { replace: true, state: { accountDisabled: true } });
      return;
    }

    // An ordinary refusal changes nothing: the session is still valid and the
    // user stays exactly where they are. Only the server may end a session.
    disableOnce.current = false;
    setDisableBusy(false);
    setDisablePhrase('');
    setDisableError(
      DISABLE_REASON_MESSAGES[result.reason ?? ''] ??
      'That request could not be completed. Nothing has changed.',
    );
  }

  // ── The return leg ────────────────────────────────────────────────────────
  // Google redirects to /app/security?link=pending, and the association is only
  // created by this same-origin call.
  //
  // THE GUARD IS A REF, NOT STATE, AND IT IS CLAIMED BEFORE THE AWAIT. React
  // StrictMode replays effects, and a state flag set after an await would not
  // have landed before the replay reads it — producing two finalize calls for
  // one ceremony. The server is single-use and would reject the second, but
  // relying on a security boundary to paper over a client bug would mean the
  // user sees a spurious failure for a link that actually succeeded.
  const finalizeOnce = useRef(false);

  useEffect(() => {
    const marker = new URLSearchParams(location.search).get('link');
    if (!marker) return;

    // Consume the marker immediately so a refresh cannot re-enter this branch,
    // and so a transient state never lingers in the URL or in history.
    const clearMarker = () => navigate('/app/security', { replace: true });

    if (marker === 'failed') {
      setLinkNotice('Google could not be linked. Please try again.');
      clearMarker();
      return;
    }
    if (marker !== 'pending') { clearMarker(); return; }

    if (finalizeOnce.current) return;
    finalizeOnce.current = true;

    (async () => {
      const result = await finalizeGoogleLink();
      clearMarker();
      if (!result.ok) {
        setLinkNotice('Google could not be linked. Please try again.');
        return;
      }
      if (result.reauthenticationRequired) {
        // The link IS durable. Saying it failed would be false and would push
        // the user to repeat a ceremony that already succeeded.
        setLinkNotice('Google was linked. Please sign in again to continue.');
        setTimeout(() => navigate('/login', { replace: true }), 2500);
        return;
      }
      setLinkNotice(result.alreadyLinked ? 'Google is already linked.' : 'Google was linked.');
      // Re-read the authoritative session rather than assuming the new state:
      // the server regenerated the session, and the capabilities it now
      // reports are the only ones worth rendering.
      await refreshSession();
    })();
  }, [location.search, finalizeGoogleLink, refreshSession, navigate]);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword,     setNewPassword]     = useState('');
  const [confirm,         setConfirm]         = useState('');
  const [error,   setError]   = useState<string | null>(null);
  const [done,    setDone]    = useState(false);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    // Checked here purely so the user is not charged a round trip for a typo.
    // The server remains the authority on every rule that matters.
    if (newPassword !== confirm) { setError('New passwords do not match.'); return; }

    setLoading(true);
    const result = await changePassword(currentPassword, newPassword);
    setLoading(false);

    if (result.ok) {
      setCurrentPassword(''); setNewPassword(''); setConfirm('');
      setDone(true);
      if (result.reauthenticationRequired) {
        setTimeout(() => navigate('/login', { replace: true }), 2500);
      }
      return;
    }
    setError(
      REASON_MESSAGES[result.reason ?? ''] ??
      result.detail ??
      'Could not change the password. Please try again.',
    );
  }

  return (
    <div style={{ padding: 24, fontFamily: "'Courier New', Courier, monospace", color: '#E8EDF5' }}>
      <div style={{ fontSize: 11, letterSpacing: 3, color: '#C9A84C', marginBottom: 4 }}>SECURITY</div>

      {/* ── Email verification (P2-D5) ─────────────────────────────────────
          Status and one action. Not an account-settings panel: no profile
          editing, no email change, no lifecycle controls (D-P2AC-13). */}
      <div style={{ fontSize: 10, letterSpacing: 1, color: '#3A5070', marginTop: 18, marginBottom: 10 }}>
        EMAIL ADDRESS
      </div>
      <div style={{ maxWidth: 420, marginBottom: 28, fontSize: 12, lineHeight: 1.7 }}>
        {user?.emailVerified ? (
          <div style={{ color: '#6FCF97' }}>Verified.</div>
        ) : (
          <>
            <div style={{ color: '#E8EDF5' }}>
              Not yet verified. Verifying confirms you control this address; it
              does not change anything else about your account.
            </div>
            {verifySent && (
              <div style={{ color: '#6FCF97', marginTop: 10 }}>
                Verification email sent. Open the link to finish.
              </div>
            )}
            {verifyError && (
              <div style={{ color: '#E86F6F', marginTop: 10, fontSize: 11 }}>{verifyError}</div>
            )}
            {!verifySent && (
              <button
                type="button" onClick={handleVerify} disabled={verifyLoading}
                style={{
                  marginTop: 12, padding: '8px 16px', fontSize: 10, letterSpacing: 2,
                  background: 'transparent', color: '#C9A84C',
                  border: '1px solid #C9A84C', borderRadius: 3,
                  fontFamily: 'inherit', fontWeight: 700,
                  cursor: verifyLoading ? 'default' : 'pointer',
                }}
              >
                {verifyLoading ? 'SENDING…' : 'SEND VERIFICATION EMAIL'}
              </button>
            )}
          </>
        )}
      </div>

      {/* ── Linked accounts (P2-D6) ────────────────────────────────────────
          Status and at most one action. Like the section above it, this is
          NOT an account-settings panel.

          Both states are gated on a positively proven capability. There is
          deliberately no unlink control: unlink is P2-D6 IMPL-4 and its
          credential_epoch semantics are not yet banked, so offering it here
          would promise a flow that does not exist.

          Nothing identifying the provider identity is rendered — no subject,
          no provider email, no token. "Linked" is the whole of what a user
          needs and the whole of what is safe to say. */}
      <div style={{ fontSize: 10, letterSpacing: 1, color: '#3A5070', marginTop: 32, marginBottom: 10 }}>
        LINKED ACCOUNTS
      </div>

      {linkNotice && (
        <div style={{ fontSize: 12, color: '#6FCF97', lineHeight: 1.7, maxWidth: 420, marginBottom: 12 }}>
          {linkNotice}
        </div>
      )}

      <div style={{ fontSize: 12, color: '#E8EDF5', lineHeight: 1.7, maxWidth: 420, marginBottom: 12 }}>
        Google —{' '}
        <span style={{ color: user?.hasGoogleIdentity === true ? '#6FCF97' : '#3A5070' }}>
          {user?.hasGoogleIdentity === true ? 'Linked' : 'Not linked'}
        </span>
      </div>

      {user?.canLinkGoogle === true && !showLinkForm && (
        <button
          type="button" onClick={() => { setLinkError(null); setShowLinkForm(true); }}
          style={{
            marginBottom: 24, padding: '8px 16px', fontSize: 10, letterSpacing: 2,
            background: 'transparent', color: '#C9A84C',
            border: '1px solid #C9A84C', borderRadius: 3,
            fontFamily: 'inherit', fontWeight: 700, cursor: 'pointer',
          }}
        >
          LINK GOOGLE
        </button>
      )}

      {user?.canUnlinkGoogle === true && !showUnlinkForm && (
        <button
          type="button" onClick={() => { setUnlinkError(null); setShowUnlinkForm(true); }}
          style={{
            marginBottom: 24, padding: '8px 16px', fontSize: 10, letterSpacing: 2,
            background: 'transparent', color: '#4A6FA5',
            border: '1px solid #1A3050', borderRadius: 3,
            fontFamily: 'inherit', fontWeight: 700, cursor: 'pointer',
          }}
        >
          UNLINK GOOGLE
        </button>
      )}

      {user?.canUnlinkGoogle === true && showUnlinkForm && (
        <form onSubmit={handleUnlink} style={{ maxWidth: 420, marginBottom: 24 }}>
          {/* Removing an authentication method revokes every session, which is
              the banked D-P2AK-5 behaviour. Saying so here is the difference
              between a security feature and an unexplained sign-out. */}
          <div style={{ fontSize: 11, color: '#4A6FA5', lineHeight: 1.6, marginBottom: 12 }}>
            Removing Google will sign you out everywhere else. Confirm your
            current password to continue.
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={LABEL} htmlFor="unlink-current-password">CURRENT PASSWORD</label>
            <input
              id="unlink-current-password" type="password" style={INPUT} autoComplete="current-password"
              value={unlinkPassword} onChange={e => setUnlinkPassword(e.target.value)} required
            />
          </div>

          {unlinkError && (
            <div style={{ fontSize: 11, color: '#E86F6F', marginBottom: 16, lineHeight: 1.6 }}>
              {unlinkError}
            </div>
          )}

          <button
            type="submit" disabled={unlinkBusy}
            style={{
              padding: '10px 20px', fontSize: 11, letterSpacing: 2,
              background: 'transparent',
              color: unlinkBusy ? '#3A5070' : '#E86F6F',
              border: `1px solid ${unlinkBusy ? '#1A3050' : '#E86F6F'}`,
              borderRadius: 3, fontFamily: 'inherit',
              fontWeight: 700, cursor: unlinkBusy ? 'default' : 'pointer',
            }}
          >
            {unlinkBusy ? 'REMOVING…' : 'CONFIRM REMOVE GOOGLE'}
          </button>
        </form>
      )}

      {user?.canLinkGoogle === true && showLinkForm && (
        <form onSubmit={handleStartLink} style={{ maxWidth: 420, marginBottom: 24 }}>
          {/* Adding an authentication method is a credential event, so the
              existing credential is proved first (D-P2AJ-3). A live session is
              not enough — a stolen one must not be able to attach an identity. */}
          <div style={{ fontSize: 11, color: '#4A6FA5', lineHeight: 1.6, marginBottom: 12 }}>
            Confirm your current password to continue to Google.
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={LABEL} htmlFor="link-current-password">CURRENT PASSWORD</label>
            <input
              id="link-current-password" type="password" style={INPUT} autoComplete="current-password"
              value={linkPassword} onChange={e => setLinkPassword(e.target.value)} required
            />
          </div>

          {linkError && (
            <div style={{ fontSize: 11, color: '#E86F6F', marginBottom: 16, lineHeight: 1.6 }}>
              {linkError}
            </div>
          )}

          <button
            type="submit" disabled={linkBusy}
            style={{
              padding: '10px 20px', fontSize: 11, letterSpacing: 2,
              background: linkBusy ? '#1A3050' : '#C9A84C',
              color: linkBusy ? '#3A5070' : '#080F1C',
              border: 'none', borderRadius: 3, fontFamily: 'inherit',
              fontWeight: 700, cursor: linkBusy ? 'default' : 'pointer',
            }}
          >
            {linkBusy ? 'CONTINUING…' : 'CONTINUE TO GOOGLE'}
          </button>
        </form>
      )}

      {/* ── Change password (P2-D4), shown only when APPLICABLE (P2-D1) ────────
          A Google-only account has no local password, so there is no current
          password to verify and the server answers 409
          PASSWORD_CREDENTIAL_NOT_CONFIGURED. Offering the form would be
          offering a dead end.

          THIS IS NOT A POLICY DECISION HIDDEN IN A UI CONDITION. The policy is
          banked at D-P2AF-4; this only reflects the account's actual credential
          state.

          FAIL CLOSED. Only a positively proven true presents the control —
          false, undefined, absent and any transient capability failure all
          withhold it. D-P2AF-4's "DO NOT PRESENT" is unconditional, so an
          unknown capability cannot be treated as a present one. The earlier
          not-equal-false form presented the control whenever the field was
          missing, which is the one case the requirement most needs to cover.

          Deliberately NOT here: a "Set Password" control. Creating a first
          password is a distinct account-lifecycle operation deferred to P2-D14
          at D-P2AF-3, and it earns no Phase-9 UX credit either way. */}
      {user?.hasPasswordCredential === true && (
      <>
      <div style={{ fontSize: 10, letterSpacing: 1, color: '#3A5070', marginBottom: 24 }}>
        CHANGE YOUR PASSWORD
      </div>

      {done && (
        <div style={{ fontSize: 12, color: '#6FCF97', lineHeight: 1.7, maxWidth: 420 }}>
          Your password has been changed. Any other signed-in sessions have been
          signed out.
        </div>
      )}

      {!done && (
        <form onSubmit={handleSubmit} style={{ maxWidth: 420 }}>
          <div style={{ marginBottom: 16 }}>
            <label style={LABEL} htmlFor="current-password">CURRENT PASSWORD</label>
            <input
              id="current-password" type="password" style={INPUT} autoComplete="current-password"
              value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} required
            />
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={LABEL} htmlFor="new-password">NEW PASSWORD</label>
            <input
              id="new-password" type="password" style={INPUT} autoComplete="new-password"
              value={newPassword} onChange={e => setNewPassword(e.target.value)} required
            />
          </div>

          <div style={{ marginBottom: 20 }}>
            <label style={LABEL} htmlFor="confirm-password">CONFIRM NEW PASSWORD</label>
            <input
              id="confirm-password" type="password" style={INPUT} autoComplete="new-password"
              value={confirm} onChange={e => setConfirm(e.target.value)} required
            />
          </div>

          {error && (
            <div style={{ fontSize: 11, color: '#E86F6F', marginBottom: 16, lineHeight: 1.6 }}>
              {error}
            </div>
          )}

          <button
            type="submit" disabled={loading}
            style={{
              padding: '10px 20px', fontSize: 11, letterSpacing: 2,
              background: loading ? '#1A3050' : '#C9A84C',
              color: loading ? '#3A5070' : '#080F1C',
              border: 'none', borderRadius: 3, fontFamily: 'inherit',
              fontWeight: 700, cursor: loading ? 'default' : 'pointer',
            }}
          >
            {loading ? 'CHANGING…' : 'CHANGE PASSWORD'}
          </button>
        </form>
      )}
      </>
      )}

      {/* ── Account lifecycle (P2-D14) ─────────────────────────────────────
          LAST, and in its own region. This is not a credential-management
          action like the three above it — it does not change how you sign in,
          it ends the account — so it is separated from them deliberately
          rather than appearing as one more thing you can do to your password.

          FAIL CLOSED, and strictly. Only a positively proven `true` presents
          the control: false, undefined, absent and any transient capability
          read failure all withhold it. The server computes the whole policy —
          active, non-terminal, password-backed, not the sole active
          administrator — and the client does not recompute a single clause of
          it, because a destructive control is the worst possible place for the
          two to disagree.

          There is no disabled-but-visible button for an ineligible account.
          Showing a greyed-out destructive control tells a sole administrator
          that the restriction is something they might get around, which is
          both untrue and an invitation to try.

          The server remains the authoritative second boundary regardless: it
          re-evaluates every condition inside the transaction. */}
      {user?.canDisableAccount === true && (
      <>
      <div style={{
        borderTop: '1px solid #1A3050', marginTop: 40, paddingTop: 28,
      }}>
        <div style={{ fontSize: 10, letterSpacing: 1, color: '#3A5070', marginBottom: 10 }}>
          ACCOUNT LIFECYCLE
        </div>

        {!showDisableForm && (
          <div style={{ maxWidth: 420, fontSize: 12, lineHeight: 1.7 }}>
            {/* Says DISABLED, never deleted. The user row, linked identities,
                preferences and watchlists are all retained — claiming deletion
                would be false, and would also read as a privacy-erasure
                request, which this is not. */}
            <div style={{ color: '#E8EDF5' }}>
              Permanently disabling your account ends your access to MER and
              signs out every session immediately. This is permanent and cannot
              be undone — not by you and not by an administrator. Your account
              record and associated data are retained; this is not a deletion
              or a data-erasure request.
            </div>
            <button
              type="button"
              onClick={() => { setDisableError(null); setShowDisableForm(true); }}
              style={{
                marginTop: 14, padding: '8px 16px', fontSize: 10, letterSpacing: 2,
                background: 'transparent', color: '#E86F6F',
                border: '1px solid #E86F6F', borderRadius: 3,
                fontFamily: 'inherit', fontWeight: 700, cursor: 'pointer',
              }}
            >
              PERMANENTLY DISABLE ACCOUNT
            </button>
          </div>
        )}

        {/* The confirmation ceremony. Reached only by the button above, and it
            is the only place the API can be called from. */}
        {showDisableForm && (
          <form onSubmit={handleDisableAccount} style={{ maxWidth: 420 }}>
            <div style={{ fontSize: 12, color: '#E86F6F', lineHeight: 1.7, marginBottom: 16 }}>
              This permanently disables your account. You will be signed out of
              every session and will not be able to sign in again. It cannot be
              undone.
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={LABEL} htmlFor="disable-current-password">CURRENT PASSWORD</label>
              <input
                id="disable-current-password" type="password" style={INPUT}
                autoComplete="current-password"
                value={disablePassword} onChange={e => setDisablePassword(e.target.value)} required
              />
            </div>

            {/* The exact phrase the server compares against, rendered from the
                shared constant so the two cannot drift. Typing it is a guard
                against an accidental click, not against an attacker — the real
                authorization is the session, CSRF and the password above. */}
            <div style={{ marginBottom: 16 }}>
              <label style={LABEL} htmlFor="disable-confirm-phrase">
                TYPE {ACCOUNT_DISABLE_CONFIRMATION} TO CONFIRM
              </label>
              <input
                id="disable-confirm-phrase" type="text" style={INPUT} autoComplete="off"
                value={disablePhrase} onChange={e => setDisablePhrase(e.target.value)} required
              />
            </div>

            {disableError && (
              <div style={{ fontSize: 11, color: '#E86F6F', marginBottom: 16, lineHeight: 1.6 }}>
                {disableError}
              </div>
            )}

            <button
              type="submit"
              disabled={disableBusy || disablePhrase !== ACCOUNT_DISABLE_CONFIRMATION}
              style={{
                padding: '10px 20px', fontSize: 11, letterSpacing: 2,
                background: 'transparent',
                color: disableBusy ? '#3A5070' : '#E86F6F',
                border: `1px solid ${disableBusy ? '#1A3050' : '#E86F6F'}`,
                borderRadius: 3, fontFamily: 'inherit', fontWeight: 700,
                cursor: disableBusy ? 'default' : 'pointer',
              }}
            >
              {disableBusy ? 'DISABLING…' : 'CONFIRM PERMANENT DISABLE'}
            </button>

            <button
              type="button" onClick={cancelDisable} disabled={disableBusy}
              style={{
                marginLeft: 12, padding: '10px 20px', fontSize: 11, letterSpacing: 2,
                background: 'transparent', color: '#4A6FA5',
                border: '1px solid #1A3050', borderRadius: 3,
                fontFamily: 'inherit', fontWeight: 700,
                cursor: disableBusy ? 'default' : 'pointer',
              }}
            >
              CANCEL
            </button>
          </form>
        )}
      </div>
      </>
      )}
    </div>
  );
}
